import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SyncIcon from "@mui/icons-material/Sync";
import QueuePlayNextIcon from "@mui/icons-material/QueuePlayNext";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideoSettingsIcon from "@mui/icons-material/VideoSettings";
import PersonIcon from "@mui/icons-material/Person";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import HistoryIcon from "@mui/icons-material/History";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import BrushIcon from "@mui/icons-material/Brush";
import CloseIcon from "@mui/icons-material/Close";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import EmailIcon from "@mui/icons-material/Email";
import { toast } from "react-toastify";
import api from "../../conf/api";
import { QueueStatus, ApprovalStatus } from "./Enums/enums";
import MRSSThumbnailCreator from "./components/MRSSThumbnailCreator";

// ─── Status design tokens ────────────────────────────────────────────────────
const STATUS = {
  [QueueStatus.PENDING]:               { label: "Pending",          color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", icon: HourglassEmptyIcon },
  [QueueStatus.RUNNING]:               { label: "Running",          color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe", icon: SyncIcon },
  [QueueStatus.COMPLETED]:             { label: "Completed",        color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0", icon: CheckCircleIcon },
  [QueueStatus.FAILED]:                { label: "Failed",           color: "#ef4444", bg: "#fff5f5", border: "#fca5a5", icon: ErrorIcon },
  [ApprovalStatus.PENDING_APPROVAL]:   { label: "Awaiting Approval",color: "#8b5cf6", bg: "#f5f3ff", border: "#ddd6fe", icon: AccessTimeIcon },
  [ApprovalStatus.REJECTED]:           { label: "Rejected",         color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: ErrorIcon },
};

// ─── CSV export column definitions ───────────────────────────────────────────
const EXPORT_COLUMNS = [
  { key: "id",               label: "ID" },
  { key: "video_unique_key", label: "WGID" },
  { key: "video_title",      label: "Video Title" },
  { key: "feed_name",        label: "Feed" },
  { key: "brand_name",       label: "Brand" },
  { key: "username",         label: "User" },
  { key: "status",           label: "Status" },
  { key: "approval_status",  label: "Approval Status" },
  { key: "process_status",   label: "Process Status" },
  { key: "retry_count",      label: "Retry Count" },
  { key: "created_date",     label: "Created Date" },
];

// Feed names are free text (often just the brand name), so label each feed
// by brand + type, e.g. "Testing Social" / "Testing Partner".
const feedLabel = (f) => f && `${f.brand_name || f.name} ${f.feed_type === "PARTNER" ? "Partner" : "Social"}`;

// Shows thumbnail as an overlay when the video is paused (poster only works before first play)
function VideoPlayer({ src, poster }) {
  const [isPaused, setIsPaused] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  return (
    <Box sx={{ position: "relative", bgcolor: "#000", lineHeight: 0 }}>
      <video
        src={src}
        controls
        poster={poster || undefined}
        onPlay={() => { setHasStarted(true); setIsPaused(false); }}
        onPause={() => setIsPaused(true)}
        style={{ width: "100%", maxHeight: 500, display: "block", outline: "none" }}
      />
      {poster && isPaused && hasStarted && (
        <Box
          component="img"
          src={poster}
          alt=""
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "calc(100% - 50px)", // match video height, leave controls row visible
            objectFit: "contain",
            bgcolor: "#000",
            pointerEvents: "none",
          }}
        />
      )}
    </Box>
  );
}

function QueuePreview() {
  const currentUser = useSelector((state) => state.user);
  const isAdmin = currentUser?.is_staff || currentUser?.is_superuser;

  const [queueItems, setQueueItems]   = useState([]);
  const [queueStats, setQueueStats]   = useState({ pending: 0, running: 0, completed: 0, failed: 0, pending_approval: 0, total: 0 });
  const [loading, setLoading]         = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBrand, setFilterBrand]   = useState("all");
  const [filterFeed, setFilterFeed]     = useState("all");
  const [brands, setBrands]             = useState([]);
  const [feeds, setFeeds]               = useState([]);
  const [exporting, setExporting]       = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState(EXPORT_COLUMNS.map((c) => c.key));
  const [searchInput, setSearchInput]   = useState("");
  const [searchQuery, setSearchQuery]   = useState("");
  const [dateFrom, setDateFrom]         = useState("");
  const [dateTo, setDateTo]             = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalRows, setTotalRows]       = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, queueId: null, confirmText: "", row: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, queueId: null, reason: "", deleteMedia: true });
  const [previewDialog, setPreviewDialog] = useState({ open: false, row: null, selectedPlatform: null });
  const [downloadMenu, setDownloadMenu] = useState({ anchorEl: null, urls: {}, singleUrl: null });
  const [autoRefreshCountdown, setAutoRefreshCountdown] = useState(30);
  const [retryingRenderId, setRetryingRenderId] = useState(null);
  const [retryingQueueId, setRetryingQueueId] = useState(null);
  const [approvalThumbnails, setApprovalThumbnails] = useState({ global: { base64: null, preview: null }, platforms: {} });
  const [thumbnailEditorOpen, setThumbnailEditorOpen] = useState(false);
  const [thumbnailEditorTarget, setThumbnailEditorTarget] = useState(null); // null = global, or "YOUTUBE" etc.
  const thumbnailFileInputRef = useRef(null);
  const thumbnailFileTargetRef = useRef(null);
  const countdownRef = useRef(null);
  // Always points to the latest fetchQueue so the auto-refresh interval never
  // uses a stale closure that captured an old paginationModel.page.
  const fetchQueueRef = useRef(null);

  const hasActiveFilters = searchQuery || dateFrom || dateTo || filterStatus !== "all" || filterBrand !== "all" || filterFeed !== "all";

  // ─── Boot ────────────────────────────────────────────────────────────────
  useEffect(() => { fetchBrands(); }, []);

  // Feed list is scoped to the selected brand — refetch whenever the brand
  // filter changes, and drop any feed selection that no longer applies.
  useEffect(() => {
    fetchFeeds(filterBrand !== "all" ? filterBrand : undefined);
    setFilterFeed("all");
  }, [filterBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep ref in sync with the latest fetchQueue on every render so the
  // auto-refresh interval always fetches the current page, not a stale one.

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [filterStatus, filterBrand, filterFeed, searchQuery, dateFrom, dateTo]);

  useEffect(() => {
    fetchQueue();
    fetchQueueStats();
  }, [filterStatus, filterBrand, filterFeed, searchQuery, dateFrom, dateTo, paginationModel.page, paginationModel.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 30s with live countdown.
  // Uses fetchQueueRef.current so the interval always fetches whatever page
  // the user is currently on, not the page captured when filters last changed.
  useEffect(() => {
    setAutoRefreshCountdown(30);
    countdownRef.current = setInterval(() => {
      setAutoRefreshCountdown((prev) => {
        if (prev <= 1) {
          fetchQueueRef.current();
          fetchQueueStats();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [filterStatus, filterBrand, filterFeed, searchQuery, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Data fetchers ───────────────────────────────────────────────────────
  const buildParams = () => {
    const p = {};
    // "PENDING_APPROVAL" is a virtual status — map to approval_status filter
    if (filterStatus === ApprovalStatus.PENDING_APPROVAL) {
      p.approval_status = ApprovalStatus.PENDING_APPROVAL;
    } else if (filterStatus !== "all") {
      p.status = filterStatus;
    }
    if (filterBrand  !== "all") p.brand  = filterBrand;
    if (filterFeed   !== "all") p.feed   = filterFeed;
    if (searchQuery.trim())     p.search  = searchQuery.trim();
    if (dateFrom)               p.date_from = dateFrom;
    if (dateTo)                 p.date_to   = dateTo;
    return p;
  };

  const fetchQueue = async () => {
    try {
      setLoading(true);
      const response = await api.get("/mrss/queue/", {
        params: { ...buildParams(), page: paginationModel.page + 1, page_size: paginationModel.pageSize },
      });
      const data = response.data;
      setQueueItems(data.results ?? data);
      setTotalRows(data.count ?? data.length ?? 0);
    } catch (error) {
      console.error("Error fetching queue:", error);
      toast.error("Failed to load queue");
    } finally {
      setLoading(false);
    }
  };
  // Always keep ref current so the interval never uses a stale closure.
  fetchQueueRef.current = fetchQueue;

  const fetchQueueStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get("/mrss/queue/stats/", { params: buildParams() });
      setQueueStats(response.data);
    } catch (error) {
      console.error("Error fetching queue stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get("/mrss/brands/", { params: { page_size: 100 } });
      setBrands(response.data.results || response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const fetchFeeds = async (brand) => {
    try {
      const params = { page_size: 500 };
      if (brand) params.brand = brand;
      const response = await api.get("/mrss/feeds/", { params });
      const list = response.data.results || response.data;
      setFeeds([...list].sort((a, b) => feedLabel(a).localeCompare(feedLabel(b))));
    } catch (error) {
      console.error("Error fetching feeds:", error);
    }
  };

  const toggleExportColumn = (key) => {
    setSelectedExportColumns((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const toggleSelectAllExportColumns = () => {
    setSelectedExportColumns((prev) => prev.length === EXPORT_COLUMNS.length ? [] : EXPORT_COLUMNS.map((c) => c.key));
  };

  // Exports every row matching the active filters (not just the page on screen)
  // by requesting the API's max page size, then serializes only the columns the
  // user picked in the export dialog to a downloadable CSV.
  const handleExportCsv = async () => {
    if (!selectedExportColumns.length) { toast.error("Select at least one column to export"); return; }
    try {
      setExporting(true);
      setExportDialogOpen(false);
      const response = await api.get("/mrss/queue/", {
        params: { ...buildParams(), page: 1, page_size: 10000 },
      });
      const rows = response.data.results ?? response.data ?? [];
      if (!rows.length) { toast.info("No records to export"); return; }
      const totalMatching = response.data.count ?? rows.length;
      if (totalMatching > rows.length) {
        toast.warn(`Exporting first ${rows.length} of ${totalMatching} matching records — narrow the filters to export the rest`);
      }

      const activeColumns = EXPORT_COLUMNS.filter((c) => selectedExportColumns.includes(c.key));
      const headers = activeColumns.map((c) => c.label);
      const csvRows = rows.map((r) => activeColumns.map((c) =>
        c.key === "video_title" ? (r.video_title || r.video_unique_key) : r[c.key]
      ));
      const escapeCell = (cell) => {
        const value = cell === null || cell === undefined ? "" : String(cell);
        return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
      };
      const csvContent = [headers, ...csvRows].map((row) => row.map(escapeCell).join(",")).join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `publishing-queue-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} record${rows.length !== 1 ? "s" : ""}`);
    } catch (error) {
      console.error("Error exporting queue:", error);
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  // ─── Actions ─────────────────────────────────────────────────────────────
  /**
   * Manually re-queue a FAILED item.
   *
   * Uses the dedicated retry endpoint rather than a plain status write,
   * because that endpoint also RESETS retry_count. The hourly scheduler only
   * picks up items with retry_count < max_retries, so an item that burned its
   * 3 automatic attempts would otherwise be re-queued and then never retried
   * again if it failed once more.
   */
  const handleRetry = async (queueId) => {
    setRetryingQueueId(queueId);
    try {
      const { data } = await api.post(`/mrss/queue/${queueId}/retry/`);
      toast.success(
        data?.cleared_attempts
          ? `Re-queued — cleared ${data.cleared_attempts} failed attempt${data.cleared_attempts === 1 ? "" : "s"}`
          : "Re-queued for publishing"
      );
      fetchQueue(); fetchQueueStats();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to retry");
    } finally {
      setRetryingQueueId(null);
    }
  };

  /**
   * Re-submit one platform's failed edited-video render. Starts a brand new
   * render job rather than resuming — a failed render has nothing to resume
   * from, and the poller only advances jobs that are still rendering, so
   * without this the queue item stays blocked on it indefinitely.
   */
  const handleRetryRender = async (renderId, platform) => {
    setRetryingRenderId(renderId);
    try {
      const { data } = await api.post(`/mrss/platform-renders/${renderId}/retry/`);
      toast.success(
        `${platform} re-render submitted — ${data.retries_remaining} attempt(s) left`
      );
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to re-render ${platform}`);
    } finally {
      setRetryingRenderId(null);
    }
  };

  const handleApprove = async (queueId) => {
    try {
      const payload = {};
      if (approvalThumbnails.global.base64) payload.thumbnail_base64 = approvalThumbnails.global.base64;
      const platformThumbs = {};
      for (const [pl, t] of Object.entries(approvalThumbnails.platforms)) {
        if (t.base64) platformThumbs[pl] = t.base64;
      }
      if (Object.keys(platformThumbs).length) payload.platform_thumbnails = platformThumbs;

      await api.post(`/mrss/queue/${queueId}/approve/`, payload);
      const hasThumb = payload.thumbnail_base64 || Object.keys(platformThumbs).length;
      toast.success(hasThumb ? "Approved with custom thumbnail(s)" : "Approved — scheduler will pick it up shortly");
      setApprovalThumbnails({ global: { base64: null, preview: null }, platforms: {} });
      fetchQueue(); fetchQueueStats();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to approve");
    }
  };

  const handleThumbnailFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const base64 = dataUrl.split(",")[1];
      const target = thumbnailFileTargetRef.current;
      if (target && target !== "global") {
        setApprovalThumbnails((prev) => ({ ...prev, platforms: { ...prev.platforms, [target]: { base64, preview: dataUrl } } }));
      } else {
        setApprovalThumbnails((prev) => ({ ...prev, global: { base64, preview: dataUrl } }));
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleThumbnailFromEditor = (base64) => {
    const preview = base64.startsWith("data:") ? base64 : `data:image/jpeg;base64,${base64}`;
    const raw = base64.startsWith("data:") ? base64.split(",")[1] : base64;
    if (thumbnailEditorTarget && thumbnailEditorTarget !== "global") {
      setApprovalThumbnails((prev) => ({ ...prev, platforms: { ...prev.platforms, [thumbnailEditorTarget]: { base64: raw, preview } } }));
    } else {
      setApprovalThumbnails((prev) => ({ ...prev, global: { base64: raw, preview } }));
    }
  };

  const openThumbnailEditor = (target) => {
    setThumbnailEditorTarget(target);
    setThumbnailEditorOpen(true);
  };

  const openThumbnailFilePicker = (target) => {
    thumbnailFileTargetRef.current = target;
    thumbnailFileInputRef.current?.click();
  };

  const clearApprovalThumbnail = (target) => {
    if (target && target !== "global") {
      setApprovalThumbnails((prev) => ({ ...prev, platforms: { ...prev.platforms, [target]: { base64: null, preview: null } } }));
    } else {
      setApprovalThumbnails((prev) => ({ ...prev, global: { base64: null, preview: null } }));
    }
  };

  const handleRejectOpen  = (queueId) => setRejectDialog({ open: true, queueId, reason: "", deleteMedia: true });
  const handleRejectCancel = () => setRejectDialog({ open: false, queueId: null, reason: "", deleteMedia: true });

  const handleRejectConfirm = async () => {
    try {
      await api.post(`/mrss/queue/${rejectDialog.queueId}/reject/`, {
        reason: rejectDialog.reason,
        delete_media: rejectDialog.deleteMedia,
      });
      toast.success(rejectDialog.deleteMedia ? "Queue item rejected and media deleted" : "Queue item rejected");
      handleRejectCancel();
      fetchQueue(); fetchQueueStats();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reject");
    }
  };

  const handlePreviewOpen = (row) => {
    const platforms = Object.keys(row.platform_render_urls || {});
    setPreviewDialog({ open: true, row, selectedPlatform: platforms[0] || null });
  };
  const handlePreviewClose = () => {
    setPreviewDialog({ open: false, row: null, selectedPlatform: null });
    setApprovalThumbnails({ global: { base64: null, preview: null }, platforms: {} });
  };

  const handleDeleteClick  = (row) => setDeleteDialog({ open: true, queueId: row.id, confirmText: "", row });
  const handleDeleteCancel = () => setDeleteDialog({ open: false, queueId: null, confirmText: "", row: null });

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/mrss/queue/${deleteDialog.queueId}/`);
      toast.success("Queue item deleted");
      handleDeleteCancel();
      fetchQueue(); fetchQueueStats();
    } catch { toast.error("Failed to delete"); }
  };

  const handleResetStuck = async () => {
    if (!window.confirm(`Reset all ${queueStats.running} stuck RUNNING item(s) to PENDING?`)) return;
    try {
      const response = await api.post("/mrss/reset-stuck-queue/");
      toast.success(response.data.message);
      fetchQueue(); fetchQueueStats();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to reset stuck items");
    }
  };

  const handleClearFilters = () => {
    setSearchInput(""); setSearchQuery(""); setDateFrom(""); setDateTo("");
    setFilterStatus("all"); setFilterBrand("all"); setFilterFeed("all");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadClick = (event, row) => {
    const platformUrls = row.platform_video_urls || {};
    const s3Url = row.s3_video_url || null;
    const allUrls = { ...platformUrls };
    if (s3Url && !Object.values(allUrls).includes(s3Url)) allUrls["Default"] = s3Url;
    const uniqueUrls = Object.entries(allUrls).filter(([, url]) => url);
    if (!uniqueUrls.length) { toast.info("No video URL available"); return; }
    if (uniqueUrls.length === 1) { triggerDownload(uniqueUrls[0][1], row.video_unique_key); }
    else { setDownloadMenu({ anchorEl: event.currentTarget, urls: Object.fromEntries(uniqueUrls), singleUrl: null }); }
  };

  const triggerDownload = (url, filename) => {
    const a = document.createElement("a");
    a.href = url; a.download = filename ? `${filename}.mp4` : "video.mp4";
    a.target = "_blank"; a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleManualRefresh = () => {
    fetchQueue(); fetchQueueStats();
    setAutoRefreshCountdown(30);
  };

  // ─── Approval status chip helper ─────────────────────────────────────────
  const ApprovalChip = ({ row }) => {
    const { approval_status, renders_ready, render_pending_reason } = row;

    if (approval_status === ApprovalStatus.PENDING_APPROVAL) {
      const s = STATUS[ApprovalStatus.PENDING_APPROVAL];
      const Icon = s.icon;
      return (
        <Chip
          icon={<Icon sx={{ fontSize: "13px !important", color: `${s.color} !important` }} />}
          label={s.label}
          size="small"
          sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: 11,
            border: `1px solid ${s.border}`, "& .MuiChip-icon": { color: s.color } }}
        />
      );
    }

    if (approval_status === ApprovalStatus.REJECTED) {
      const s = STATUS[ApprovalStatus.REJECTED];
      const Icon = s.icon;
      return (
        <Chip
          icon={<Icon sx={{ fontSize: "13px !important", color: `${s.color} !important` }} />}
          label="Rejected"
          size="small"
          sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: 11,
            border: `1px solid ${s.border}`, "& .MuiChip-icon": { color: s.color } }}
        />
      );
    }

    // APPROVED — show normal queue status
    const s = STATUS[row.status] || {};
    const Icon = s.icon || HourglassEmptyIcon;
    const isRunning = row.status === QueueStatus.RUNNING;
    return (
      <Box display="flex" flexDirection="column" gap={0.4}>
        <Chip
          icon={
            <Icon sx={{
              fontSize: "13px !important",
              color: `${s.color} !important`,
              ...(isRunning ? { animation: "spin 1.5s linear infinite", "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } } } : {}),
            }} />
          }
          label={s.label}
          size="small"
          sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: 11,
            border: `1px solid ${s.border}`, "& .MuiChip-icon": { color: s.color } }}
        />
        {!renders_ready && (
          <Chip
            icon={<VideoSettingsIcon sx={{ fontSize: "11px !important", color: "#d97706 !important" }} />}
            label="Render pending"
            size="small"
            sx={{ bgcolor: "#fffbeb", color: "#d97706", fontWeight: 600, fontSize: 10,
              border: "1px solid #fde68a", height: 18, "& .MuiChip-icon": { color: "#d97706" } }}
          />
        )}
      </Box>
    );
  };

  // ─── Columns ─────────────────────────────────────────────────────────────
  const columns = [
    {
      field: "id",
      headerName: "#",
      width: 64,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "video_title",
      headerName: "Video",
      flex: 1.4,
      minWidth: 200,
      renderCell: (params) => {
        const initials = (params.row.video_title || "V").slice(0, 2).toUpperCase();
        const hue = [...(params.row.video_unique_key || "X")].reduce((n, c) => n + c.charCodeAt(0), 0) % 360;
        const thumb = params.row.video_thumbnail_url;
        return (
          <Box display="flex" alignItems="center" gap={1.5} py={0.5}>
            {thumb ? (
              <Box component="img" src={thumb} alt=""
                sx={{ width: 48, height: 36, borderRadius: 1, flexShrink: 0,
                  objectFit: "cover", border: "1px solid", borderColor: "divider" }} />
            ) : (
              <Avatar sx={{ width: 48, height: 36, borderRadius: 1, flexShrink: 0,
                background: `hsl(${hue},55%,42%)`, fontSize: 13, fontWeight: 700 }}>
                {initials}
              </Avatar>
            )}
            <Box minWidth={0}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {params.row.video_title || params.row.video_unique_key || "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: "monospace", fontSize: 10 }}>
                {params.row.video_unique_key}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "feed_name",
      headerName: "Feed / Brand",
      flex: 0.9,
      minWidth: 140,
      renderCell: (params) => (
        <Box minWidth={0}>
          <Typography variant="body2" fontWeight={500} noWrap>{params.value}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{params.row.brand_name}</Typography>
        </Box>
      ),
    },
    {
      field: "username",
      headerName: "Submitted By",
      width: 140,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box sx={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
            bgcolor: alpha("#3b82f6", 0.12), display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PersonIcon sx={{ fontSize: 14, color: "#3b82f6" }} />
          </Box>
          <Typography variant="caption" fontWeight={600} noWrap color="text.primary">
            {params.value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "approval_status",
      headerName: "Status",
      width: 155,
      renderCell: (params) => <ApprovalChip row={params.row} />,
    },
    {
      field: "process_status",
      headerName: "Message",
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => {
        const isPendingApproval = params.row.approval_status === ApprovalStatus.PENDING_APPROVAL;
        const { renders_ready, render_pending_reason } = params.row;
        return (
          <Box minWidth={0} width="100%">
            <Tooltip title={params.value || ""} placement="top">
              <Typography
                variant="caption"
                noWrap
                color={params.value ? "text.primary" : "text.disabled"}
                sx={{ display: "block", width: "100%" }}
              >
                {isPendingApproval && !renders_ready
                  ? "⏳ Waiting for video render before approval is possible"
                  : params.value || "—"}
              </Typography>
            </Tooltip>
            {isPendingApproval && !renders_ready && render_pending_reason && (
              <Tooltip title={render_pending_reason}>
                <Typography variant="caption" noWrap color="#d97706" sx={{ display: "block", width: "100%", fontSize: 10 }}>
                  {render_pending_reason}
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "retry_count",
      headerName: "Retries",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const exhausted = params.value >= params.row.max_retries;
        return (
          <Chip
            label={`${params.value}/${params.row.max_retries}`}
            size="small"
            sx={{
              fontWeight: 700, fontSize: 11,
              bgcolor: exhausted ? alpha("#ef4444", 0.1) : alpha("#6b7280", 0.08),
              color: exhausted ? "#ef4444" : "#6b7280",
              border: `1px solid ${exhausted ? alpha("#ef4444", 0.3) : alpha("#6b7280", 0.2)}`,
            }}
          />
        );
      },
    },
    {
      field: "created_date",
      headerName: "Created",
      width: 130,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {new Date(params.value).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: isAdmin ? 210 : 160,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const { approval_status, renders_ready, render_pending_reason, status: qStatus } = params.row;
        const isPendingApproval = approval_status === ApprovalStatus.PENDING_APPROVAL;
        const hasUrls = Object.keys(params.row.platform_video_urls || {}).length > 0 || !!params.row.s3_video_url;
        // Any FAILED item can be retried by hand. Deliberately NOT gated on
        // retry_count < max_retries: that budget governs the AUTOMATIC hourly
        // sweep, and hiding the button once it is spent is exactly backwards —
        // an item nobody can rescue is precisely the one an operator needs to
        // re-queue after fixing the cause. The endpoint resets the counter.
        const attemptsUsed = params.row.retry_count ?? 0;
        const maxAttempts = params.row.max_retries ?? 3;
        const autoRetriesSpent = attemptsUsed >= maxAttempts;
        const canRetry = qStatus === QueueStatus.FAILED
          && approval_status !== ApprovalStatus.REJECTED;

        const hasPreviewUrl = !!(
          params.row.video_preview_url ||
          Object.keys(params.row.platform_render_urls || {}).length > 0 ||
          Object.keys(params.row.platform_video_urls || {}).length > 0
        );
        return (
          <Box display="flex" alignItems="center" gap={0.25} justifyContent="flex-end">
            {/* Preview button */}
            <Tooltip title={hasPreviewUrl ? "Preview video" : "No video available for preview"}>
              <span>
                <IconButton size="small" disabled={!hasPreviewUrl} onClick={() => handlePreviewOpen(params.row)}
                  sx={{ p: 0.75, borderRadius: 1, "&:hover": { bgcolor: alpha("#8b5cf6", 0.1) }, "&.Mui-disabled": { opacity: 0.3 } }}>
                  <PlayCircleIcon sx={{ fontSize: 16, color: hasPreviewUrl ? "#8b5cf6" : "#94a3b8" }} />
                </IconButton>
              </span>
            </Tooltip>

            {/* Approve button — admin only, only when PENDING_APPROVAL */}
            {isAdmin && isPendingApproval && (
              <Tooltip title={
                !renders_ready
                  ? `Cannot approve yet — render still in progress: ${render_pending_reason || "unknown platform"}`
                  : "Approve — allow scheduler to process this item"
              }>
                <span>
                  <IconButton
                    size="small"
                    disabled={!renders_ready}
                    onClick={() => handleApprove(params.row.id)}
                    sx={{ p: 0.75, borderRadius: 1,
                      "&:hover": { bgcolor: alpha("#10b981", 0.1) },
                      "&.Mui-disabled": { opacity: 0.35 } }}
                  >
                    <ThumbUpIcon sx={{ fontSize: 16, color: renders_ready ? "#10b981" : "#94a3b8" }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Reject button — admin only, only when PENDING_APPROVAL */}
            {isAdmin && isPendingApproval && (
              <Tooltip title="Reject — prevent this item from being published">
                <IconButton size="small" onClick={() => handleRejectOpen(params.row.id)}
                  sx={{ p: 0.75, borderRadius: 1, "&:hover": { bgcolor: alpha("#ef4444", 0.1) } }}>
                  <ThumbDownIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                </IconButton>
              </Tooltip>
            )}

            {canRetry && (
              <Tooltip
                title={
                  autoRetriesSpent
                    ? `Retry manually — all ${maxAttempts} automatic retries are used up. This clears the counter and re-queues it.`
                    : `Retry now — re-queue for publishing (${attemptsUsed}/${maxAttempts} automatic retries used)`
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={retryingQueueId === params.row.id}
                    onClick={() => handleRetry(params.row.id)}
                    sx={{ p: 0.75, borderRadius: 1,
                      "&:hover": { bgcolor: alpha("#3b82f6", 0.1) },
                      "&.Mui-disabled": { opacity: 0.4 } }}
                  >
                    {retryingQueueId === params.row.id ? (
                      <CircularProgress size={16} sx={{ color: "#3b82f6" }} />
                    ) : (
                      <ReplayIcon sx={{ fontSize: 16, color: autoRetriesSpent ? "#f59e0b" : "#3b82f6" }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={hasUrls ? "Download video" : "No video URL available"}>
              <span>
                <IconButton size="small" disabled={!hasUrls} onClick={(e) => handleDownloadClick(e, params.row)}
                  sx={{ p: 0.75, borderRadius: 1, "&:hover": { bgcolor: alpha("#10b981", 0.1) }, "&.Mui-disabled": { opacity: 0.3 } }}>
                  <DownloadIcon sx={{ fontSize: 16, color: hasUrls ? "#10b981" : "#94a3b8" }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete item">
              <IconButton size="small" onClick={() => handleDeleteClick(params.row)}
                sx={{ p: 0.75, borderRadius: 1, "&:hover": { bgcolor: alpha("#ef4444", 0.1) } }}>
                <DeleteIcon sx={{ fontSize: 16, color: "#ef4444" }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // ─── Stat tile (inside hero) ─────────────────────────────────────────────
  const HeroStat = ({ statusKey, value, label, color, bg, icon: Icon, lightColor }) => {
    const isActive = filterStatus === statusKey;
    return (
      <Box sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: "rgba(255,255,255,0.12)",
        backdropFilter: "blur(4px)", border: "1px solid rgba(255,255,255,0.2)",
        display: "flex", alignItems: "center", gap: 1, flex: "1 1 auto", minWidth: 110,
        cursor: isActive ? "default" : "pointer",
        outline: isActive ? "2px solid rgba(255,255,255,0.6)" : "none",
        transition: "all 0.15s",
        "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
      }}
      onClick={() => setFilterStatus(isActive ? "all" : statusKey)}
      >
        <Icon sx={{ fontSize: 18, color: lightColor || color }} />
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.7, display: "block", lineHeight: 1 }}>
            {label}
          </Typography>
          {statsLoading
            ? <Skeleton width={30} height={22} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
            : <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>{value}</Typography>}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── Hero Header ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        mb: 3, p: 3, borderRadius: 3, overflow: "hidden", position: "relative",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        color: "white",
      }}>
        <Box sx={{ position: "absolute", top: -50, right: -50, width: 220, height: 220,
          borderRadius: "50%", bgcolor: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: -70, right: 100, width: 160, height: 160,
          borderRadius: "50%", bgcolor: "rgba(255,255,255,0.03)", pointerEvents: "none" }} />

        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <QueuePlayNextIcon sx={{ fontSize: 28, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight={700} letterSpacing={-0.5}>Publishing Queue</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography variant="body2" sx={{ opacity: 0.6 }}>
                {totalRows} job{totalRows !== 1 ? "s" : ""}
                {hasActiveFilters ? " · filtered" : " total"}
              </Typography>
              <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: "rgba(255,255,255,0.1)",
                display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#86efac",
                  animation: "pulse 2s infinite", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } } }} />
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Auto-refresh in {autoRefreshCountdown}s
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap">
            {queueStats.running > 0 && (
              <Button variant="outlined" startIcon={<RestoreIcon />} onClick={handleResetStuck}
                sx={{ color: "#fde68a", borderColor: "rgba(253,230,138,0.4)", fontWeight: 600,
                  "&:hover": { borderColor: "#fde68a", bgcolor: "rgba(253,230,138,0.08)" } }}>
                Reset Stuck ({queueStats.running})
              </Button>
            )}
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleManualRefresh} disabled={loading}
              sx={{ color: "white", borderColor: "rgba(255,255,255,0.35)",
                "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.08)" } }}>
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Clickable stat tiles */}
        <Box display="flex" gap={1.5} mt={2.5} flexWrap="wrap">
          {/* Awaiting Approval — shown to all (admins need it most) */}
          <HeroStat
            statusKey={ApprovalStatus.PENDING_APPROVAL}
            value={queueStats.pending_approval ?? 0}
            label="Awaiting Approval"
            color="#8b5cf6"
            lightColor="#c4b5fd"
            icon={AccessTimeIcon}
          />
          <HeroStat statusKey={QueueStatus.PENDING}   value={queueStats.pending}   label="Pending"   color="#f59e0b" lightColor="#fde68a" icon={HourglassEmptyIcon} />
          <HeroStat statusKey={QueueStatus.RUNNING}   value={queueStats.running}   label="Running"   color="#3b82f6" lightColor="#93c5fd" icon={SyncIcon} />
          <HeroStat statusKey={QueueStatus.COMPLETED} value={queueStats.completed} label="Completed" color="#10b981" lightColor="#86efac" icon={CheckCircleIcon} />
          <HeroStat statusKey={QueueStatus.FAILED}    value={queueStats.failed}    label="Failed"    color="#ef4444" lightColor="#fca5a5" icon={ErrorIcon} />

          {/* Total */}
          <Box sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.15)", display: "flex", alignItems: "center", gap: 1,
            flex: "1 1 auto", minWidth: 110 }}>
            <OndemandVideoIcon sx={{ fontSize: 18, opacity: 0.6 }} />
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.6, display: "block", lineHeight: 1 }}>Total</Typography>
              {statsLoading
                ? <Skeleton width={30} height={22} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
                : <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>{queueStats.total ?? totalRows}</Typography>}
            </Box>
          </Box>
        </Box>

        {/* Admin notice */}
        {isAdmin && queueStats.pending_approval > 0 && (
          <Box sx={{ mt: 2, px: 2, py: 1, borderRadius: 1.5, bgcolor: "rgba(139,92,246,0.15)",
            border: "1px solid rgba(139,92,246,0.4)", display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 16, color: "#c4b5fd" }} />
            <Typography variant="caption" sx={{ color: "#e9d5ff", fontWeight: 600 }}>
              {queueStats.pending_approval} item{queueStats.pending_approval !== 1 ? "s" : ""} waiting for your approval before they enter the MRSS feed.
            </Typography>
          </Box>
        )}

        {/* Progress bar while loading */}
        {loading && (
          <LinearProgress sx={{ position: "absolute", bottom: 0, left: 0, right: 0, borderRadius: 0,
            bgcolor: "rgba(255,255,255,0.1)", "& .MuiLinearProgress-bar": { bgcolor: "#93c5fd" } }} />
        )}
      </Paper>

      {/* ── Filter Toolbar ───────────────────────────────────────────────────── */}
      <Paper elevation={0} variant="outlined"
        sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: "white", borderColor: "#e2e8f0" }}>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <FilterListIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">Filters</Typography>
          {hasActiveFilters && (
            <Chip label="Filters active" size="small" color="primary" variant="outlined"
              sx={{ ml: "auto", height: 22, fontSize: 11 }} />
          )}
        </Box>

        <Grid container spacing={1.5} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" placeholder="Search by video title or ID…"
              value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8fafc",
                "&:hover fieldset": { borderColor: "#3b82f6" }, "&.Mui-focused fieldset": { borderColor: "#3b82f6" } } }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearchInput(""); setSearchQuery(""); }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }} />
          </Grid>

          {/* Status */}
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}>
                <MenuItem value="all"><em>All Status</em></MenuItem>
                <MenuItem value={ApprovalStatus.PENDING_APPROVAL}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#8b5cf6" }} />
                    Awaiting Approval
                  </Box>
                </MenuItem>
                {Object.values(QueueStatus).map((st) => (
                  <MenuItem key={st} value={st}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS[st]?.color }} />
                      {STATUS[st]?.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Brand */}
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Brand</InputLabel>
              <Select label="Brand" value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}>
                <MenuItem value="all"><em>All Brands</em></MenuItem>
                {brands.map((b) => <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {/* Feed */}
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Feed</InputLabel>
              <Select label="Feed" value={filterFeed} onChange={(e) => setFilterFeed(e.target.value)}
                renderValue={(v) => v === "all" ? <em>All Feeds</em> : feedLabel(feeds.find((f) => f.id === v)) || v}
                sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}>
                <MenuItem value="all"><em>All Feeds</em></MenuItem>
                {feeds.map((f) => (
                  <MenuItem key={f.id} value={f.id} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <Typography variant="body2" fontWeight={600}>{feedLabel(f)}</Typography>
                    <Typography variant="caption" color="text.secondary">{f.name}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Date From */}
          <Grid item xs={6} sm={4} md={2}>
            <TextField fullWidth size="small" label="From" type="date" value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 14, color: "action.active" }} /></InputAdornment> }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: dateFrom ? alpha("#3b82f6", 0.04) : "#f8fafc" } }} />
          </Grid>

          {/* Date To */}
          <Grid item xs={6} sm={4} md={2}>
            <TextField fullWidth size="small" label="To" type="date" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 14, color: "action.active" }} /></InputAdornment> }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: dateTo ? alpha("#3b82f6", 0.04) : "#f8fafc" } }} />
          </Grid>

          {/* Clear */}
          <Grid item xs={12} sm="auto" md="auto">
            <Button fullWidth size="small" startIcon={<ClearIcon />} onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              variant={hasActiveFilters ? "contained" : "outlined"} color={hasActiveFilters ? "warning" : "inherit"}
              sx={{ height: 40, borderRadius: 2, fontWeight: 600, whiteSpace: "nowrap",
                ...(hasActiveFilters ? {} : { color: "text.disabled", borderColor: "#e2e8f0" }) }}>
              Clear
            </Button>
          </Grid>
        </Grid>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <Box display="flex" gap={0.75} flexWrap="wrap" mt={1.5} pt={1.5} sx={{ borderTop: "1px dashed #e2e8f0" }}>
            {filterStatus !== "all" && (
              <Chip size="small" variant="filled" color="primary" onDelete={() => setFilterStatus("all")}
                label={`Status: ${STATUS[filterStatus]?.label || filterStatus}`}
                sx={{ height: 24, fontSize: 11, fontWeight: 600 }} />
            )}
            {filterBrand !== "all" && (
              <Chip size="small" variant="filled" color="primary" onDelete={() => setFilterBrand("all")}
                label={`Brand: ${filterBrand}`} sx={{ height: 24, fontSize: 11, fontWeight: 600 }} />
            )}
            {filterFeed !== "all" && (
              <Chip size="small" variant="filled" color="primary" onDelete={() => setFilterFeed("all")}
                label={`Feed: ${feedLabel(feeds.find((f) => f.id === filterFeed)) || filterFeed}`}
                sx={{ height: 24, fontSize: 11, fontWeight: 600 }} />
            )}
            {searchQuery && (
              <Chip size="small" variant="filled" color="primary" onDelete={() => { setSearchInput(""); setSearchQuery(""); }}
                label={`"${searchQuery}"`} sx={{ height: 24, fontSize: 11, fontWeight: 600 }} />
            )}
            {dateFrom && (
              <Chip size="small" variant="filled" color="primary" onDelete={() => setDateFrom("")}
                label={`From: ${dateFrom}`} sx={{ height: 24, fontSize: 11, fontWeight: 600 }} />
            )}
            {dateTo && (
              <Chip size="small" variant="filled" color="primary" onDelete={() => setDateTo("")}
                label={`To: ${dateTo}`} sx={{ height: 24, fontSize: 11, fontWeight: 600 }} />
            )}
          </Box>
        )}
      </Paper>

      {/* ── Data Grid ────────────────────────────────────────────────────────── */}
      <Paper elevation={0} variant="outlined"
        sx={{ borderRadius: 2, overflow: "hidden", bgcolor: "white", borderColor: "#e2e8f0" }}>
        <Box display="flex" justifyContent="flex-end" alignItems="center"
          sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0", bgcolor: "white" }}>
          <Button variant="outlined" size="small"
            startIcon={exporting ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <FileDownloadIcon fontSize="small" />}
            onClick={() => setExportDialogOpen(true)} disabled={exporting}
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none", color: "#334155", borderColor: "#cbd5e1",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" } }}>
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </Box>
        <DataGrid
          rows={queueItems}
          columns={columns}
          loading={loading}
          rowCount={totalRows}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          rowHeight={60}
          getRowHeight={(params) => {
            // Taller rows for pending-approval items that may show render status sub-text
            return params.model.approval_status === ApprovalStatus.PENDING_APPROVAL ? 72 : 60;
          }}
          sx={{
            border: "none",
            fontFamily: "inherit",
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0",
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 },
            },
            "& .MuiDataGrid-row": {
              "&:hover": { bgcolor: "#f8fafc" },
              borderBottom: "1px solid #f1f5f9",
            },
            "& .MuiDataGrid-row[data-approval='PENDING_APPROVAL']": {
              bgcolor: alpha("#8b5cf6", 0.03),
            },
            "& .MuiDataGrid-cell": { borderBottom: "none", alignItems: "center", display: "flex" },
            "& .MuiDataGrid-footerContainer": { bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" },
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            "& .MuiDataGrid-virtualScroller": { minHeight: 200 },
          }}
        />
      </Paper>

      {/* ── Export Column Selection Dialog ───────────────────────────────────── */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <FileDownloadIcon fontSize="small" color="action" />
          Export CSV — Choose Columns
        </DialogTitle>
        <DialogContent dividers sx={{ py: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            All {totalRows} record{totalRows !== 1 ? "s" : ""} matching the current filters will be exported with the columns you select.
          </Typography>
          <FormControlLabel
            label={<Typography fontWeight={600}>Select All</Typography>}
            control={
              <Checkbox
                checked={selectedExportColumns.length === EXPORT_COLUMNS.length}
                indeterminate={selectedExportColumns.length > 0 && selectedExportColumns.length < EXPORT_COLUMNS.length}
                onChange={toggleSelectAllExportColumns}
              />
            }
          />
          <Divider sx={{ my: 1 }} />
          <Box display="flex" flexDirection="column">
            {EXPORT_COLUMNS.map((c) => (
              <FormControlLabel key={c.key} label={c.label}
                control={
                  <Checkbox checked={selectedExportColumns.includes(c.key)} onChange={() => toggleExportColumn(c.key)} />
                }
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExportDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleExportCsv} disabled={!selectedExportColumns.length || exporting}
            startIcon={exporting ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <FileDownloadIcon fontSize="small" />}>
            {exporting ? "Exporting…" : "Export"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ────────────────────────────────────────── */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ px: 3, py: 2.5, background: "linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)",
            color: "white", display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.15)", width: 38, height: 38 }}>
              <WarningAmberIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>Delete Queue Item</Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>This action cannot be undone</Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {deleteDialog.row && (
            <>
              {/* Item details */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: "#fca5a5", bgcolor: "#fff5f5" }}>
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {deleteDialog.row.video_title || deleteDialog.row.video_unique_key}
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {deleteDialog.row.video_unique_key && (
                    <Typography variant="caption" color="text.secondary">
                      Key: <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{deleteDialog.row.video_unique_key}</span>
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Feed: <strong>{deleteDialog.row.feed_name}</strong>
                  </Typography>
                  {deleteDialog.row.brand_name && (
                    <Typography variant="caption" color="text.secondary">
                      Brand: <strong>{deleteDialog.row.brand_name}</strong>
                    </Typography>
                  )}
                </Box>
              </Paper>

              {/* Published platforms */}
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Published Platforms</Typography>
              {deleteDialog.row.social_media_posts?.length > 0 ? (
                <Box display="flex" flexDirection="column" gap={0.75} mb={2}>
                  {deleteDialog.row.social_media_posts.map((post) => {
                    const p = (post.platform || "").toLowerCase();
                    let PlatIcon = null;
                    if (p.includes("facebook")) PlatIcon = FacebookIcon;
                    else if (p.includes("instagram")) PlatIcon = InstagramIcon;
                    else if (p.includes("twitter") || p.includes("x")) PlatIcon = TwitterIcon;
                    else if (p.includes("youtube")) PlatIcon = YouTubeIcon;
                    return (
                      <Box key={post.id} sx={{ p: 1.25, bgcolor: "#f8fafc", borderRadius: 1.5,
                        border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ color: "#64748b" }}>{PlatIcon && <PlatIcon fontSize="small" />}</Box>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="caption" fontWeight={700} display="block" sx={{ textTransform: "capitalize" }}>
                            {post.platform}
                          </Typography>
                          {post.post_url
                            ? <Link href={post.post_url} target="_blank" rel="noopener" variant="caption">View Post</Link>
                            : post.platform_post_id
                              ? <Typography variant="caption" color="text.disabled">{post.platform_post_id}</Typography>
                              : null}
                        </Box>
                        {post.posted_date && (
                          <Typography variant="caption" color="text.disabled">
                            {new Date(post.posted_date).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ p: 1.5, mb: 2, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px dashed #e2e8f0", textAlign: "center" }}>
                  <Typography variant="caption" color="text.disabled">Not posted to any social platform</Typography>
                </Box>
              )}

              {/* Partner email preview */}
              {deleteDialog.row.partner_email_config && (
                <Box mb={2}>
                  <Divider sx={{ mb: 2 }} />
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <EmailIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight={700}>Partner Email Notification</Typography>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#c7d2fe", bgcolor: "#eef2ff" }}>
                    {deleteDialog.row.partner_email_config.subject ? (
                      <>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
                          SUBJECT
                        </Typography>
                        <Typography variant="body2" fontWeight={600} mb={1.5}>
                          {deleteDialog.row.partner_email_config.subject}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.disabled" display="block" mb={1}>
                        No email subject configured
                      </Typography>
                    )}
                    {deleteDialog.row.partner_email_config.body ? (
                      <>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
                          MESSAGE
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.secondary", fontSize: 13 }}>
                          {deleteDialog.row.partner_email_config.body}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        No email body configured
                      </Typography>
                    )}
                    {deleteDialog.row.partner_email_config.template_id && (
                      <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                        Template: {deleteDialog.row.partner_email_config.template_id}
                      </Typography>
                    )}
                  </Paper>
                  <Alert severity="info" sx={{ mt: 1, borderRadius: 1.5 }}>
                    <Typography variant="caption">
                      This email will be sent to the partner when email sending is enabled.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </>
          )}

          <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
            The queue item and its associated published feed item will be
            <strong> permanently deleted</strong>. Social media posts will <strong>not</strong> be removed from the platforms.
          </Alert>
          <Box sx={{ p: 2, bgcolor: "#fef3c7", borderRadius: 1.5, border: "1px solid #fde68a", mb: 1.5 }}>
            <Typography variant="body2" fontWeight={600} color="#92400e">
              Type <code style={{ background: "#fde68a", padding: "1px 5px", borderRadius: 3 }}>delete</code> to confirm:
            </Typography>
          </Box>
          <TextField
            autoFocus fullWidth size="small"
            placeholder='Type "delete" to confirm'
            value={deleteDialog.confirmText}
            onChange={(e) => setDeleteDialog((prev) => ({ ...prev, confirmText: e.target.value }))}
            onPaste={(e) => e.preventDefault()}
            error={deleteDialog.confirmText.length > 0 && deleteDialog.confirmText !== "delete"}
            helperText={deleteDialog.confirmText.length > 0 && deleteDialog.confirmText !== "delete" ? 'Must be exactly "delete"' : " "}
            autoComplete="off"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fff5f5", borderTop: "1px solid #fca5a5", gap: 1 }}>
          <Button onClick={handleDeleteCancel} variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error"
            disabled={deleteDialog.confirmText.toLowerCase() !== "delete"}
            startIcon={<DeleteIcon />}
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 700,
              background: deleteDialog.confirmText === "delete" ? "linear-gradient(135deg, #7f1d1d, #ef4444)" : undefined }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reject Confirmation Dialog ────────────────────────────────────────── */}
      <Dialog open={rejectDialog.open} onClose={handleRejectCancel} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ px: 3, py: 2.5, background: "linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)",
            color: "white", display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.15)", width: 38, height: 38 }}>
              <ThumbDownIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>Reject Queue Item</Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>Video will not enter the MRSS feed</Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Optionally provide a reason for rejection (visible in the queue status message).
          </Typography>
          <TextField
            autoFocus fullWidth size="small" multiline rows={2}
            placeholder="Reason for rejection (optional)"
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 }, mb: 2 }}
          />

          {/* Delete media option */}
          <Box sx={{ p: 1.5, borderRadius: 1.5, border: "1px solid",
            borderColor: rejectDialog.deleteMedia ? alpha("#ef4444", 0.4) : "#e2e8f0",
            bgcolor: rejectDialog.deleteMedia ? alpha("#ef4444", 0.04) : "#fafafa",
            transition: "all 0.15s" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rejectDialog.deleteMedia}
                  onChange={(e) => setRejectDialog((prev) => ({ ...prev, deleteMedia: e.target.checked }))}
                  size="small"
                  color="error"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600} color={rejectDialog.deleteMedia ? "error.main" : "text.primary"}>
                    Delete video media from storage
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Removes S3 files and the video record if not used elsewhere
                  </Typography>
                </Box>
              }
            />
            {rejectDialog.deleteMedia && (
              <Box display="flex" alignItems="flex-start" gap={0.75} mt={1}
                sx={{ pl: 3.5, color: "#dc2626" }}>
                <DeleteSweepIcon sx={{ fontSize: 15, mt: 0.1, flexShrink: 0 }} />
                <Typography variant="caption" fontWeight={500}>
                  This will permanently delete the uploaded/rendered video files. This cannot be undone.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fff5f5", borderTop: "1px solid #fca5a5", gap: 1 }}>
          <Button onClick={handleRejectCancel} variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleRejectConfirm} variant="contained" color="error"
            startIcon={rejectDialog.deleteMedia ? <DeleteSweepIcon /> : <ThumbDownIcon />}
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 700 }}>
            {rejectDialog.deleteMedia ? "Reject & Delete Media" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Video Preview Dialog ─────────────────────────────────────────────── */}
      {previewDialog.open && previewDialog.row && (() => {
        const row = previewDialog.row;
        const isPendingApproval = row.approval_status === ApprovalStatus.PENDING_APPROVAL;

        // Platforms allowed for this feed type — brand-publish saves ALL selected platforms
        // (social + partner) into platform_video_sources for every queue item, so we must
        // restrict to platforms that actually belong to this feed's type.
        const SOCIAL_PLATFORMS = new Set(["YOUTUBE","TIKTOK","FACEBOOK","INSTAGRAM","PINTEREST","SNAPCHAT","THREADS"]);
        const PARTNER_PLATFORMS = new Set(["DAILYMOTION","MSN","OTHERS"]);
        const allowedPlatforms = row.feed_type === "PARTNER" ? PARTNER_PLATFORMS : SOCIAL_PLATFORMS;

        // Scope platform_video_sources to this feed's type only
        const rawPvs = row.platform_video_sources || {};
        const pvs = Object.fromEntries(Object.entries(rawPvs).filter(([pl]) => allowedPlatforms.has(pl)));
        const pvsKeys = Object.keys(pvs);
        const renderUrls    = Object.fromEntries(Object.entries(row.platform_render_urls || {}).filter(([pl]) => allowedPlatforms.has(pl)));
        const publishedUrls = row.platform_video_urls  || {};

        // Tabs are only meaningful when a platform has a distinct URL (EDITED render or LOCAL upload).
        // When platform_video_sources is present, restrict publishedUrls to THIS queue item's
        // platforms only — prevents historical FeedItem platform_video_urls from bleeding in.
        // ORIGINAL / DUPLICATE platforms have no render URL → don't get a tab → main video shows.
        const filteredPublished = pvsKeys.length > 0
          ? Object.fromEntries(Object.entries(publishedUrls).filter(([pl]) => pvs[pl]))
          : publishedUrls;

        const allPlatformUrls = { ...filteredPublished, ...renderUrls };
        // platform -> when VRE deleted that render's file. The backend has
        // already swapped the preview over to our own archived copy; this is
        // only so the dialog says so rather than quietly showing a different
        // file. A platform with no archived copy has no URL at all, and falls
        // through to the empty state below.
        const rendersExpired = row.platform_renders_expired || {};
        const platformKeys = Object.keys(allPlatformUrls);
        
        // Show tabs for all selected platforms, or fallback to distinct URL platforms
        const displayPlatformKeys = pvsKeys.length > 0 ? pvsKeys : platformKeys;
        const hasTabs = displayPlatformKeys.length > 0;
        const activePlatform = hasTabs
          ? (previewDialog.selectedPlatform || displayPlatformKeys[0])
          : null;

        const activeUrl = activePlatform && allPlatformUrls[activePlatform]
          ? allPlatformUrls[activePlatform]
          : row.video_preview_url;

        let activeThumbnailUrl = row.video_thumbnail_url;
        if (activePlatform && pvs[activePlatform]) {
            const thumbSource = pvs[activePlatform].thumbnail_source || "original";
            if (thumbSource === "none") {
                activeThumbnailUrl = null;
            } else if (thumbSource === "custom") {
                activeThumbnailUrl = (row.platform_thumbnail_preview_urls || {})[activePlatform] || null;
            } else {
                activeThumbnailUrl = row.video_thumbnail_url;
            }
        } else if (activePlatform) {
            activeThumbnailUrl = (row.platform_thumbnail_preview_urls || {})[activePlatform] || row.video_thumbnail_url;
        }

        return (
          <Dialog
            open={previewDialog.open}
            onClose={handlePreviewClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", bgcolor: "#0f172a" } }}
          >
            {/* Header */}
            <Box sx={{ px: 3, py: 2, borderBottom: "1px solid rgba(255,255,255,0.08)",
              display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
              {/* Thumbnail */}
              {activeThumbnailUrl && (
                <Box component="img" src={activeThumbnailUrl} alt=""
                  sx={{ width: 64, height: 48, borderRadius: 1.5, flexShrink: 0,
                    objectFit: "cover", border: "1px solid rgba(255,255,255,0.1)" }} />
              )}
              <Box minWidth={0} flex={1}>
                <Typography variant="h6" fontWeight={700} color="white" noWrap>
                  {row.video_title || row.video_unique_key || "Video Preview"}
                </Typography>
                <Box display="flex" alignItems="center" gap={1.5} mt={0.25} flexWrap="wrap">
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <PersonIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                    <Typography variant="caption" color="#94a3b8">
                      {row.username || "—"}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="#475569">·</Typography>
                  <Typography variant="caption" color="#94a3b8">{row.feed_name}</Typography>
                  <Typography variant="caption" color="#475569">·</Typography>
                  <Typography variant="caption" color="#94a3b8" sx={{ fontFamily: "monospace", fontSize: 10 }}>
                    {row.video_unique_key}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                {/* Approval status badge */}
                {isPendingApproval && (
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: "12px !important", color: "#8b5cf6 !important" }} />}
                    label="Awaiting Approval"
                    size="small"
                    sx={{ bgcolor: alpha("#8b5cf6", 0.15), color: "#c4b5fd", fontWeight: 700,
                      fontSize: 11, border: "1px solid rgba(139,92,246,0.3)" }}
                  />
                )}
                <IconButton size="small" onClick={handlePreviewClose} sx={{ color: "#64748b", "&:hover": { color: "white" } }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <DialogContent sx={{ p: 0, bgcolor: "#0f172a" }}>
              {/* Platform tab strip */}
              {hasTabs && (
                <Box sx={{ display: "flex", gap: 0.5, px: 2, py: 1.25,
                  borderBottom: "1px solid rgba(255,255,255,0.06)", overflowX: "auto" }}>
                  {displayPlatformKeys.map((pl) => {
                    const isSelected = previewDialog.selectedPlatform === pl || (!previewDialog.selectedPlatform && pl === displayPlatformKeys[0]);
                    return (
                      <Chip
                        key={pl}
                        label={pl}
                        size="small"
                        onClick={() => setPreviewDialog((prev) => ({ ...prev, selectedPlatform: pl }))}
                        sx={{
                          cursor: "pointer",
                          fontWeight: 700, fontSize: 11,
                          bgcolor: isSelected ? alpha("#8b5cf6", 0.25) : "rgba(255,255,255,0.06)",
                          color: isSelected ? "#c4b5fd" : "#94a3b8",
                          border: isSelected ? "1px solid rgba(139,92,246,0.5)" : "1px solid rgba(255,255,255,0.1)",
                          "&:hover": { bgcolor: alpha("#8b5cf6", 0.18) },
                        }}
                      />
                    );
                  })}
                </Box>
              )}

              {/* The rendered file VRE produced has passed its retention
                  window. What plays below is our own archived copy. */}
              {activePlatform && rendersExpired[activePlatform] && (
                <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", gap: 1,
                  bgcolor: alpha("#64748b", 0.18), borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <HistoryIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                  <Typography variant="caption" color="#cbd5e1">
                    The original render was deleted from the render server on{" "}
                    {new Date(rendersExpired[activePlatform]).toLocaleDateString()}
                    {activeUrl
                      ? " — playing the archived copy."
                      : " and no archived copy was kept."}
                  </Typography>
                </Box>
              )}

              {/* Video player */}
              <Box sx={{ position: "relative", bgcolor: "#000", minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {activeUrl ? (
                  <VideoPlayer
                    key={activeUrl}
                    src={activeUrl}
                    poster={activeThumbnailUrl || undefined}
                  />
                ) : (
                  <Box textAlign="center" py={4} position="relative">
                    {activeThumbnailUrl ? (
                      <Box>
                        <Box component="img" src={activeThumbnailUrl} alt="thumbnail"
                          sx={{ maxHeight: 300, maxWidth: "100%", objectFit: "contain",
                            opacity: 0.55, display: "block", mx: "auto" }} />
                        <Box sx={{ position: "absolute", inset: 0, display: "flex",
                          flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                          <VideoSettingsIcon sx={{ fontSize: 40, color: "#d97706", mb: 1 }} />
                          <Typography variant="body2" color="#fbbf24" fontWeight={600}>
                            Render in progress…
                          </Typography>
                          {row.render_pending_reason && (
                            <Typography variant="caption" color="#d97706" sx={{ mt: 0.5, px: 2, textAlign: "center" }}>
                              {row.render_pending_reason}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <BrokenImageIcon sx={{ fontSize: 48, color: "#334155", mb: 1 }} />
                        <Typography variant="body2" color="#475569">
                          No video available for preview yet
                        </Typography>
                        {row.render_pending_reason && (
                          <Typography variant="caption" color="#d97706" sx={{ display: "block", mt: 0.5 }}>
                            {row.render_pending_reason}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>

              {/* Render-pending notice. A FAILED render is called out separately
                  from one that's merely still working — a failed one never
                  recovers on its own (the poller only advances rows that are
                  still rendering), so it needs a manual re-render rather than
                  more waiting. */}
              {!row.renders_ready && (() => {
                const states = row.platform_render_states || {};
                const failed = Object.entries(states).filter(
                  ([, s]) => s.render_status === "render_failed"
                );
                const stillWorking = Object.entries(states).filter(
                  ([, s]) => s.render_status !== "render_failed" && s.render_status !== "render_complete"
                );
                return (
                  <Box sx={{ px: 3, py: 1.5, bgcolor: alpha(failed.length ? "#ef4444" : "#d97706", 0.08),
                    borderTop: `1px solid ${failed.length ? "rgba(239,68,68,0.25)" : "rgba(217,119,6,0.2)"}`,
                    display: "flex", flexDirection: "column", gap: 1 }}>

                    {failed.map(([platform, s]) => (
                      <Box key={platform} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <ErrorIcon sx={{ fontSize: 16, color: "#ef4444", flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 140 }}>
                          <Typography variant="caption" color="#f87171" fontWeight={600}>
                            {platform} render failed
                          </Typography>
                          <Typography variant="caption" color="#fca5a5" sx={{ display: "block", fontSize: 10 }}>
                            {s.auto_retrying
                              ? `Retrying automatically — attempt ${s.auto_retry_count + 1} of ${s.auto_retries_max}` +
                                (s.next_auto_retry_at
                                  ? ` at ${new Date(s.next_auto_retry_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                                  : "")
                              : s.can_retry
                              ? `Automatic retries used up — ${s.retries_remaining} of 3 manual re-render attempts left`
                              : "Retry limit reached — fix the template or payload and re-submit the publish"}
                          </Typography>
                        </Box>
                        <Tooltip title={
                          s.auto_retrying
                            ? "Retrying automatically every 5 minutes — manual retry opens after 3 automatic attempts"
                            : s.can_retry
                            ? "Start this render again from scratch"
                            : "No attempts left"
                        }>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ReplayIcon sx={{ fontSize: "14px !important" }} />}
                              disabled={!s.can_retry || retryingRenderId === s.render_id}
                              onClick={() => handleRetryRender(s.render_id, platform)}
                              sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 600,
                                py: 0.25, color: "#fca5a5", borderColor: "rgba(248,113,113,0.4)",
                                "&:hover": { borderColor: "#f87171", bgcolor: alpha("#ef4444", 0.08) } }}
                            >
                              {retryingRenderId === s.render_id ? "Submitting…" : "Re-render"}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    ))}

                    {stillWorking.length > 0 && (
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <VideoSettingsIcon sx={{ fontSize: 16, color: "#d97706", mt: 0.1, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="caption" color="#fbbf24" fontWeight={600}>
                            Video render still in progress
                          </Typography>
                          <Typography variant="caption" color="#d97706" sx={{ display: "block", fontSize: 10 }}>
                            {row.render_pending_reason}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Nothing per-platform to show (e.g. a video-level render or
                        a render record that never got created) — fall back to the
                        summary reason rather than an empty banner. */}
                    {failed.length === 0 && stillWorking.length === 0 && (
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <VideoSettingsIcon sx={{ fontSize: 16, color: "#d97706", mt: 0.1, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="caption" color="#fbbf24" fontWeight={600}>
                            Video render still in progress
                          </Typography>
                          <Typography variant="caption" color="#d97706" sx={{ display: "block", fontSize: 10 }}>
                            {row.render_pending_reason}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })()}
              {/* Change Thumbnail section — admin only, pending items */}
              {isAdmin && isPendingApproval && (() => {
                // Use the already-scoped pvs (filtered by feed type) so only relevant platforms show
                const thumbPlatforms = Object.keys(pvs);
                const isMultiPlatform = thumbPlatforms.length > 0;

                // Reusable per-entry thumbnail row
                const ThumbRow = ({ target, currentUrl, label }) => {
                  const staged = target === "global"
                    ? approvalThumbnails.global
                    : (approvalThumbnails.platforms[target] || { base64: null, preview: null });
                  const displaySrc = staged.preview || currentUrl || "";
                  return (
                    <Box display="flex" alignItems="center" gap={1.5} py={0.75}
                      sx={{ borderBottom: "1px solid rgba(255,255,255,0.04)", "&:last-child": { borderBottom: "none" } }}>
                      {label && (
                        <Typography variant="caption" color="#64748b" fontWeight={700}
                          sx={{ minWidth: 80, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          {label}
                        </Typography>
                      )}
                      {/* Thumbnail preview */}
                      <Box sx={{ position: "relative", flexShrink: 0 }}>
                        {displaySrc ? (
                          <Box component="img" src={displaySrc} alt=""
                            sx={{ width: 72, height: 46, borderRadius: 1, objectFit: "cover",
                              border: staged.preview ? "2px solid #8b5cf6" : "1px solid rgba(255,255,255,0.1)",
                              bgcolor: "#0f172a" }} />
                        ) : (
                          <Box sx={{ width: 72, height: 46, borderRadius: 1, bgcolor: "#0f172a",
                            border: "1px dashed rgba(255,255,255,0.12)", display: "flex",
                            alignItems: "center", justifyContent: "center" }}>
                            <AddPhotoAlternateIcon sx={{ fontSize: 18, color: "#334155" }} />
                          </Box>
                        )}
                        {staged.preview && (
                          <IconButton size="small" onClick={() => clearApprovalThumbnail(target)}
                            sx={{ position: "absolute", top: -7, right: -7, bgcolor: "#ef4444", color: "white",
                              width: 16, height: 16, "&:hover": { bgcolor: "#dc2626" } }}>
                            <CloseIcon sx={{ fontSize: 10 }} />
                          </IconButton>
                        )}
                      </Box>
                      {/* Buttons */}
                      <Button size="small" variant="outlined" startIcon={<AddPhotoAlternateIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() => openThumbnailFilePicker(target)}
                        sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 600, py: 0.5,
                          color: "#94a3b8", borderColor: "rgba(255,255,255,0.12)",
                          "&:hover": { borderColor: "#8b5cf6", color: "#c4b5fd", bgcolor: alpha("#8b5cf6", 0.08) } }}>
                        Upload
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<BrushIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() => openThumbnailEditor(target)}
                        sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 600, py: 0.5,
                          color: "#94a3b8", borderColor: "rgba(255,255,255,0.12)",
                          "&:hover": { borderColor: "#0d9488", color: "#5eead4", bgcolor: alpha("#0d9488", 0.08) } }}>
                        Editor
                      </Button>
                      {staged.preview && (
                        <Typography variant="caption" color="#8b5cf6" fontWeight={700} sx={{ fontSize: 10 }}>
                          Staged
                        </Typography>
                      )}
                    </Box>
                  );
                };

                return (
                  <Box sx={{ px: 3, py: 2, borderTop: "1px solid rgba(255,255,255,0.06)", bgcolor: "#1e293b" }}>
                    <input type="file" accept="image/*" ref={thumbnailFileInputRef}
                      style={{ display: "none" }} onChange={handleThumbnailFileChange} />
                    <Typography variant="caption" color="#94a3b8" fontWeight={600}
                      sx={{ textTransform: "uppercase", letterSpacing: 0.6, fontSize: 10, display: "block", mb: 1.25 }}>
                      Change Thumbnail (optional)
                    </Typography>

                    {isMultiPlatform ? (
                      // Per-platform rows — show the platform's own thumbnail when available
                      thumbPlatforms.map((pl) => {
                        let platThumbUrl = row.video_thumbnail_url;
                        const thumbSource = pvs[pl].thumbnail_source || "original";
                        if (thumbSource === "none") {
                            platThumbUrl = null;
                        } else if (thumbSource === "custom") {
                            platThumbUrl = (row.platform_thumbnail_preview_urls || {})[pl] || null;
                        } else {
                            platThumbUrl = row.video_thumbnail_url;
                        }

                        return (
                          <ThumbRow
                            key={pl}
                            target={pl}
                            currentUrl={platThumbUrl}
                            label={pl}
                          />
                        );
                      })
                    ) : (
                      // Single global picker (legacy / non-brand publish)
                      <ThumbRow target="global" currentUrl={row.video_thumbnail_url} label={null} />
                    )}
                  </Box>
                );
              })()}
            </DialogContent>

            {/* Footer — approve / reject for admins */}
            {isAdmin && isPendingApproval && (
              <DialogActions sx={{ px: 3, py: 2, bgcolor: "#1e293b", borderTop: "1px solid rgba(255,255,255,0.06)", gap: 1 }}>
                <Typography variant="caption" color="#64748b" sx={{ flex: 1 }}>
                  Review the video above then approve or reject.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ThumbDownIcon />}
                  onClick={() => { handlePreviewClose(); handleRejectOpen(row.id); }}
                  sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, color: "#f87171",
                    borderColor: "rgba(248,113,113,0.4)", "&:hover": { borderColor: "#f87171", bgcolor: alpha("#ef4444", 0.08) } }}
                >
                  Reject
                </Button>
                <Tooltip title={!row.renders_ready ? `Render still in progress: ${row.render_pending_reason}` : ""}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<ThumbUpIcon />}
                      disabled={!row.renders_ready}
                      onClick={() => { handleApprove(row.id); handlePreviewClose(); }}
                      sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 700,
                        background: row.renders_ready ? "linear-gradient(135deg, #059669, #10b981)" : undefined }}
                    >
                      {(approvalThumbnails.global.base64 || Object.values(approvalThumbnails.platforms).some(t => t.base64))
                        ? "Approve & Save Thumbnail" : "Approve"}
                    </Button>
                  </span>
                </Tooltip>
              </DialogActions>
            )}
          </Dialog>
        );
      })()}

      {/* ── Platform download picker ─────────────────────────────────────────── */}
      <Menu
        anchorEl={downloadMenu.anchorEl}
        open={Boolean(downloadMenu.anchorEl)}
        onClose={() => setDownloadMenu({ anchorEl: null, urls: {}, singleUrl: null })}
        PaperProps={{ sx: { borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 260 } }}
      >
        <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #f1f5f9" }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
            Select platform to download
          </Typography>
        </Box>
        {Object.entries(downloadMenu.urls).map(([platform, url]) => (
          <MenuItem key={platform} onClick={() => { triggerDownload(url, platform); setDownloadMenu({ anchorEl: null, urls: {}, singleUrl: null }); }}
            sx={{ py: 1.25, "&:hover": { bgcolor: alpha("#10b981", 0.06) } }}>
            <ListItemIcon>
              <Box sx={{ width: 30, height: 30, borderRadius: 1, bgcolor: alpha("#10b981", 0.1),
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <DownloadIcon sx={{ fontSize: 16, color: "#10b981" }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="body2" fontWeight={600}>{platform}</Typography>}
              secondary={url.length > 45 ? `…${url.slice(-38)}` : url}
              secondaryTypographyProps={{ variant: "caption", noWrap: true, sx: { maxWidth: 200, fontFamily: "monospace", fontSize: 10 } }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Thumbnail Editor — opened from approval dialog, target is null/global or a platform name */}
      {thumbnailEditorOpen && (() => {
        const _row = previewDialog.row;
        const _isPlatform = thumbnailEditorTarget && thumbnailEditorTarget !== "global";
        const _pvs = _row?.platform_video_sources || {};

        // Resolve the effective source platform — DUPLICATE platforms point to another platform
        const _resolvedPlatform = (() => {
          if (!_isPlatform) return null;
          const cfg = _pvs[thumbnailEditorTarget] || {};
          if (cfg.source === "DUPLICATE" && cfg.duplicate_of) return cfg.duplicate_of;
          return thumbnailEditorTarget;
        })();

        // Best video URL: prefer render URL of the resolved (source) platform,
        // then published URL, then generic preview. DUPLICATE platforms share the
        // same render as the platform they duplicate.
        const editorUrl = _isPlatform
          ? (_row?.platform_render_urls?.[_resolvedPlatform]
              || _row?.platform_video_urls?.[_resolvedPlatform]
              || _row?.platform_render_urls?.[thumbnailEditorTarget]
              || _row?.platform_video_urls?.[thumbnailEditorTarget]
              || _row?.video_preview_url
              || "")
          : (_row?.video_preview_url || "");

        // A platform is "edited" if it or its source (for DUPLICATE) used the Video Editor
        const isEditedPlatform = _isPlatform && (
          _pvs[_resolvedPlatform]?.source === "EDITED"
          || _pvs[thumbnailEditorTarget]?.source === "EDITED"
        );

        const stagedPreview = _isPlatform
          ? approvalThumbnails.platforms[thumbnailEditorTarget]?.preview
          : approvalThumbnails.global.preview;

        // For EDITED/DUPLICATE-of-EDITED with no staged thumbnail: open in video
        // mode so the user sees the edited render immediately (null = no capturedFrame)
        const editorCapturedFrame = stagedPreview
          || (isEditedPlatform ? null : (_row?.video_thumbnail_url || null));

        return (
          <MRSSThumbnailCreator
            open={thumbnailEditorOpen}
            setOpen={(val) => { setThumbnailEditorOpen(val); if (!val) setThumbnailEditorTarget(null); }}
            url={editorUrl}
            capturedFrame={editorCapturedFrame}
            onSave={handleThumbnailFromEditor}
          />
        );
      })()}
    </Box>
  );
}

export default QueuePreview;
